import moment from "moment-timezone";

/**
 * Court time. Every scheduling decision in this app is made in King County's timezone, and the
 * backend enforces it with PHP `now()` under APP_TIMEZONE=America/Los_Angeles — so the UI must
 * ask the same question, never "is it past on the viewer's clock".
 *
 * IMPORTANT — the two datetime classes in this app (see helpers/formatServerDateTime):
 *  - `created_at` / `updated_at` are serialized as UTC and must be CONVERTED to Pacific
 *  - `appointments.datetime`, `date`, `start_time` are already stored as Pacific WALL-CLOCK
 *    strings ("2026-08-18 16:48:00") and must never be converted
 * The helpers below are for that second kind: they compare wall-clock to wall-clock, so a
 * viewer in any timezone gets the same answer the server would give. Mixing a zone-aware
 * moment with a naive one is exactly what made the CNC label disagree with the API.
 */
export const PACIFIC_TZ = "America/Los_Angeles";

const WALL_CLOCK = "YYYY-MM-DD HH:mm:ss";

/** Now, as a Pacific wall-clock string. DST (PDT vs PST) is handled by the tz database. */
export const pacificNow = () => moment().tz(PACIFIC_TZ).format(WALL_CLOCK);

/** Parse a Pacific wall-clock value without applying any timezone shift. */
const asWallClock = (value) => {
  if (!value) return null;
  const parsed = moment(String(value), [WALL_CLOCK, "YYYY-MM-DD HH:mm", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed : null;
};

/** True when a Pacific wall-clock datetime is in the past in COURT time. */
export const isPastInPacific = (wallClock) => {
  const at = asWallClock(wallClock);
  return at ? at.format(WALL_CLOCK) < pacificNow() : false;
};

/** Minutes from court-time now until a Pacific wall-clock datetime (negative once past). */
export const minutesUntilInPacific = (wallClock) => {
  const at = asWallClock(wallClock);
  if (!at) return null;
  return at.diff(moment(pacificNow(), WALL_CLOCK), "minutes");
};
